import { select, takeLatest } from 'redux-saga/effects';
import { NonBilledSessionsReportActions } from '../constants';
import { NonBilledSessionsReportApi } from 'src/services/end-points';
import { StoreState } from '../reducers';
import { PaginationService } from '../../services/api-handlers/pagination';
import {
    GetNonBilledSessionsReportAction,
    GetNonBilledSessionsReportCsvAction,
} from '../actions/nonBilledSessionsReport-actions';
import { resolveApiCall, ResolverApiFailure, handleError } from '../../services/api-handlers/api-resolver';
import { exportToCsv } from '../../services/exports/csv';
import { groupNonBilledSessionsReportToCsv } from '../../helpers/nonBilledSessionsReport/groupCsv';
import { INonBilledSession } from '../reducers/nonBilledSessionsReport-reducer';
import { toast } from 'react-toastify';

const CSV_PAGE_SIZE = 5000;

function onFailure(result: ResolverApiFailure) {
    if (result.error && result.error.response && result.error.response.status) {
        toast.error(result.error.response.data?.detail ?? result.error.message ?? 'Unknown error');
    }
    handleError(result);
}

function* handleFetchNonBilledSessionsReport(action: GetNonBilledSessionsReportAction): any {
    const oldData = yield select((state: StoreState) => state.nonBilledSessionsReport.data);
    const { page, itemsPerPage, ...filters } = action.payload;
    const currentPage = page ? page : 1;

    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, currentPage, oldData, () =>
        NonBilledSessionsReportApi.getNonBilledSessionsReport({
            ...filters,
            page: currentPage,
            itemsPerPage,
            pagination: 1,
        }),
    );
}

// Walks all pages and exports them in one file, like the Billed Hours Report
function* handleFetchNonBilledSessionsReportCsv(action: GetNonBilledSessionsReportCsvAction): any {
    const { page, all, ...filters } = action.payload;
    const currentPage: number = page ? +page : 1;
    const collected: INonBilledSession[] = all ?? [];

    yield resolveApiCall(
        action,
        () =>
            NonBilledSessionsReportApi.getNonBilledSessionsReport({
                ...filters,
                page: currentPage,
                itemsPerPage: CSV_PAGE_SIZE,
                pagination: 1,
            }),
        function* (data) {
            const rows: INonBilledSession[] = [...collected, ...(data.payload ?? [])];
            if ((data.payload ?? []).length === CSV_PAGE_SIZE) {
                yield* handleFetchNonBilledSessionsReportCsv({
                    ...action,
                    payload: { ...filters, page: currentPage + 1, all: rows },
                });
            } else {
                exportToCsv('non-billed-sessions-report.csv', groupNonBilledSessionsReportToCsv(rows));
            }
        },
        onFailure,
    );
}

export default function* () {
    yield takeLatest(
        NonBilledSessionsReportActions.FETCH_NON_BILLED_SESSIONS_REPORT,
        handleFetchNonBilledSessionsReport,
    );
    yield takeLatest(
        NonBilledSessionsReportActions.FETCH_NON_BILLED_SESSIONS_REPORT_CSV,
        handleFetchNonBilledSessionsReportCsv,
    );
}
